import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DealerAuthProvider, useDealerAuth } from '../context/DealerAuthContext';
import { useTheme } from '@/context/ThemeContext';
import DealerLayout from '../components/DealerLayout';
import { fetchDealerOrders } from '@/lib/api/orders';
import type { DealerOrder } from '@/mocks/dealerOrders';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  confirmed: 'bg-green-500/20 text-green-400',
  shipped: 'bg-[#F97316]/20 text-[#F97316]',
};

const statusIcons: Record<string, string> = {
  pending: 'ri-time-line',
  confirmed: 'ri-check-double-line',
  shipped: 'ri-truck-line',
};

function DashboardContent() {
  const { theme } = useTheme();
  const { dealer } = useDealerAuth();
  const dark = theme === 'dark';
  const [orders, setOrders] = useState<DealerOrder[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const nextOrders = await fetchDealerOrders();
        setOrders(nextOrders);
      } catch {
        setOrders([]);
      }
    };

    void load();
  }, []);

  const card = dark ? 'bg-[#1C2B3A] border-white/5' : 'bg-white border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/60' : 'text-gray-500';
  const faint = dark ? 'text-white/30' : 'text-gray-400';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';
  const rowHover = dark ? 'hover:bg-white/3' : 'hover:bg-gray-50';
  const iconBg = dark ? 'bg-[#0D1B2A]' : 'bg-gray-100';
  const borderB = dark ? 'border-white/10' : 'border-gray-200';
  const editBtn = dark ? 'border-white/20 text-white/60 hover:text-white hover:border-white/40' : 'border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-400';

  const recentOrders = orders.slice(0, 4);
  const totalSpend = orders.reduce((sum, order) => sum + order.total, 0);
  const pendingCount = orders.filter((order) => order.status === 'pending').length;
  const shippedCount = orders.filter((order) => order.status === 'shipped').length;

  return (
    <div className="space-y-6">
      <div className={`${dark ? 'bg-gradient-to-r from-[#F97316]/20 to-[#1C2B3A] border-[#F97316]/20' : 'bg-gradient-to-r from-orange-50 to-white border-orange-200'} border rounded-lg px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4`}>
        <div>
          <p className="text-[#F97316] text-xs font-bold uppercase tracking-widest mb-1">Welcome back</p>
          <h2 className={`text-xl font-black ${text}`}>{dealer?.contact}</h2>
          <p className={`text-sm mt-0.5 ${muted}`}>{dealer?.name} &mdash; {dealer?.accountNumber}</p>
        </div>
        <Link
          to="/dealer/new-order"
          className="flex items-center gap-2 bg-[#F97316] text-white font-bold px-5 py-2.5 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </div>
          Place New Order
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: 'ri-file-list-3-line', label: 'Total Orders', value: orders.length.toString(), sub: 'All time' },
          { icon: 'ri-time-line', label: 'Pending', value: pendingCount.toString(), sub: 'Awaiting confirmation' },
          { icon: 'ri-truck-line', label: 'Shipped', value: shippedCount.toString(), sub: 'On the way' },
          { icon: 'ri-money-dollar-circle-line', label: 'Total Spend', value: `$${totalSpend.toLocaleString()}`, sub: 'All time' },
        ].map((stat, i) => (
          <div key={i} className={`border rounded-lg p-5 ${card}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#F97316]/20">
                <i className={`${stat.icon} text-[#F97316] text-lg`}></i>
              </div>
            </div>
            <p className={`text-2xl font-black ${text}`}>{stat.value}</p>
            <p className={`text-xs font-medium mt-0.5 ${muted}`}>{stat.label}</p>
            <p className={`text-xs mt-0.5 ${faint}`}>{stat.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`lg:col-span-2 border rounded-lg overflow-hidden ${card}`}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${borderB}`}>
            <h3 className={`font-bold text-sm ${text}`}>Recent Orders</h3>
            <Link to="/dealer/orders" className="text-[#F97316] text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap">
              View all
            </Link>
          </div>
          <div className={`divide-y ${divider}`}>
            {recentOrders.length === 0 ? (
              <div className={`px-5 py-10 text-sm ${muted}`}>Your orders will appear here once you submit them.</div>
            ) : (
              recentOrders.map((order) => (
                <div key={order.id} className={`flex items-center justify-between px-5 py-3.5 transition-colors ${rowHover}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${iconBg}`}>
                      <i className={`${statusIcons[order.status]} text-[#F97316] text-sm`}></i>
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${text}`}>{order.id}</p>
                      <p className={`text-xs mt-0.5 ${faint}`}>
                        {order.items.map((item) => `${item.productName.replace('Stingray ', '')} x${item.qty}`).join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusColors[order.status]}`}>
                      {order.status}
                    </span>
                    <p className={`text-xs mt-1 ${faint}`}>{order.createdAt}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={`border rounded-lg overflow-hidden ${card}`}>
          <div className={`px-5 py-4 border-b ${borderB}`}>
            <h3 className={`font-bold text-sm ${text}`}>Account Details</h3>
          </div>
          <div className="p-5 space-y-4">
            {[
              { icon: 'ri-building-line', label: 'Company', value: dealer?.name || '—' },
              { icon: 'ri-user-line', label: 'Contact', value: dealer?.contact || '—' },
              { icon: 'ri-mail-line', label: 'Email', value: dealer?.email || '—' },
              { icon: 'ri-phone-line', label: 'Phone', value: dealer?.phone || '—' },
              { icon: 'ri-map-pin-line', label: 'State', value: dealer?.state || '—' },
              { icon: 'ri-calendar-line', label: 'Member Since', value: dealer?.since || '—' },
            ].map((row) => (
              <div key={row.label} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center shrink-0">
                  <i className={`${row.icon} text-[#F97316] text-sm`}></i>
                </div>
                <div>
                  <p className={`text-xs ${faint}`}>{row.label}</p>
                  <p className={`text-sm font-medium mt-0.5 ${text}`}>{row.value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 pb-5">
            <Link
              to="/dealer/account"
              className={`block w-full text-center border text-xs font-semibold py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${editBtn}`}
            >
              Edit Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DealerDashboardPage() {
  return (
    <DealerAuthProvider>
      <DealerLayout title="Dashboard">
        <DashboardContent />
      </DealerLayout>
    </DealerAuthProvider>
  );
}
